import { WorldMapMarkerDetailStyle } from '__generated__/globalTypes';
import { getInitialViewState } from 'components/map/location/defaultMapLocation';
import { LazyImageFallback } from 'components/map/various/LazyImageFallback';
import { DetailModalBubble } from 'components/site/modals/DetailModalBubble';
import { CUSTOM_MARKER_BUILDERS } from 'customMarkers/customMarkerBuilders';
import { pinIconBuilder } from 'customMarkers/pinIconBuilder';
import { WorldMapMarkerStory_worldMapView_markers } from 'graphql/query/__generated__/WorldMapMarkerStory';
import { useFrameRateControl } from 'hooks/dev/useFrameRateControl';
import { useMarkerVisibilityView } from 'hooks/map/useMarkerVisibilityView';
import { usePreloadDefaultImages } from 'hooks/map/usePreloadDefaultImages';
import { useBackgroundImageMarker } from 'hooks/marker/useBackgroundImageMarker';
import { EFonts } from 'libs/styles/styling';
import { useIsWide } from 'libs/utils/logic/ui/responsiveLogic';
import {
    GraphQLStylesMapToEnum,
    mapGraphQLMarker,
    mapGraphQLMarkers
} from 'libs/utils/mapping/worldGrahQLMaps';
import {
    mapIndoorStandForViewAndEdit,
    mapVariantIconForViewAndEdit
} from 'libs/utils/marker/mapIndoorStandForViewAndEdit';
import { useAnalytics } from 'libs/utils/site/analytics';
import { uniq } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { ClickEventData, GiveMeMap, IWorldMarker } from 'react-give-me-map';
import { IMapProps, MarkersCustomConfigProps } from 'react-give-me-map/dist/utils/map/mapTypes';
import { MapRef } from 'react-map-gl';
import { CtxModal } from 'session/ctxModal';
import { CtxView } from 'session/ctxView';
import { GLOBAL_MARKER_CONFIG } from 'static/config/globalMarkerConfig';
import { Localization } from 'static/localization/localization';
import styled, { css } from 'styled-components/macro';

export interface IProps {
    onMarkerSelected?: (markerId: string) => boolean | undefined;
    onMarkerSelectedOverride?: (data: { storyId: string; markerId: string }) => void;
    onMarkersSelectedOverride?: (data: { markerIds: string[] }) => void;
    onMapClickedOverride?: () => boolean;
    mapProps?: Partial<IMapProps>;
    mapChildren?: JSX.Element;
    overrideMapMarkers?: IWorldMarker[];
    highlightedStyle?: string;
    disableSimpleGestures: boolean;
    topOverflow?: JSX.Element;
    topOverflowPosition?: 'left' | 'right';
    forceInitialPositionBounds?: boolean;
    highlightedMarkerIds?: string[];
    orderedMarkerIds?: string[];
    interactiveLayerIds?: string[];
    customClickableMarkerIds?: Set<string>;
    markersCustomConfig?: MarkersCustomConfigProps;
    onMapLoaded?: (mapRef: MapRef) => void;
    language?: string;
}

export const CustomMap = (props: IProps): JSX.Element => {
    const isWide = useIsWide();
    const ctxView = useContext(CtxView);
    const ctxModal = useContext(CtxModal);
    const markerVisibility = useMarkerVisibilityView();
    const analytics = useAnalytics();
    const backgroundImageMarker = useBackgroundImageMarker();
    const [markersOnMissClickHighlight, setMarkersOnMissClickHighlight] = useState(false);

    const extraInteractiveLayerIds = props.interactiveLayerIds ?? [];

    usePreloadDefaultImages(ctxView.mapRef);

    useFrameRateControl(ctxView.mapRef);

    const visibleStoryIds = uniq(
        (ctxView.allMarkers ?? [])
            .filter(markerVisibility.isMarkerStoryVisibleInViewList)
            .flatMap((e) => e.stories.map((story) => story.storyId))
    );

    function onMarkersSelected(markerIds: string[], clickData: ClickEventData) {
        if (!markerIds.length && props.onMapClickedOverride) {
            const result = props.onMapClickedOverride();
            if (result) {
                return;
            }
        }
        // Logic for "group" click
        if (markerIds.length > 1) {
            // INFO: Not supported by EVENT App. In Event app it always zooms-in
            if (props.onMarkersSelectedOverride) {
                props.onMarkersSelectedOverride({ markerIds });
                return;
            }

            if (!props.onMarkerSelectedOverride) {
                const markers = ctxView.allMarkers?.filter((m) =>
                    markerIds.includes(m.worldMapMarkerId)
                );
                const markerStoryIds = markers.flatMap((marker) =>
                    marker.stories.map((story) => story.storyId)
                );
                const storyIds = ctxView.conectedDetails
                    ?.filter((s) => markerStoryIds.includes(s.storyId))
                    ?.filter((e) => visibleStoryIds.includes(e.storyId))
                    ?.map((e) => e.storyId);

                if (storyIds.length) {
                    ctxModal.setDetailListModal({
                        storyIds
                    });

                    analytics.track('marker.clicked', {
                        action: 'detail_list.opened'
                    });
                    return;
                }
            }

            if (clickData) {
                ctxView.mapRef.current.flyTo({
                    zoom: ctxView.mapRef?.current?.getZoom() + 2,
                    center: [clickData.lng, clickData.lat]
                });
                return;
            }
        }

        // Logic for regular click
        const markerId = markerIds[0];
        const marker = ctxView.allMarkers?.find((m) => m.worldMapMarkerId === markerId);
        if (marker && marker.stories.length) {
            const storyIds = marker.stories.map((e) => e.storyId);
            const stories = ctxView.conectedDetails?.filter((s) => storyIds.includes(s.storyId));
            if (stories.length === 1) {
                if (props.onMarkerSelectedOverride) {
                    props.onMarkerSelectedOverride({ storyId: stories[0].storyId, markerId });
                    return;
                }
                ctxModal.setDetailModal({
                    id: stories[0].storyId,
                    detail: stories[0],
                    markerId: marker.worldMapMarkerId
                });
                ctxModal.setStoryList({
                    visibleStoryIds,
                    currentIndex: visibleStoryIds.findIndex((e) => e === storyIds[0])
                });
                analytics.track('marker.clicked', {
                    action: 'detail.opened'
                });
                return;
            } else if (stories.length >= 2) {
                ctxModal.setDetailListModal({
                    storyIds: stories.map((e) => e.storyId)
                });
                analytics.track('marker.clicked', {
                    action: 'detail_list.opened'
                });
                return;
            }
        }
        const actionDone = props.onMarkerSelected?.(markerId);
        if (actionDone) {
            analytics.track('marker.clicked', {
                action: 'custom'
            });
            return;
        }
        setMarkersOnMissClickHighlight(true);
        analytics.track('marker.clicked', {
            action: 'none'
        });
    }

    useEffect(() => {
        if (markersOnMissClickHighlight) {
            setTimeout(() => {
                setMarkersOnMissClickHighlight(false);
            }, 300);
        }
    }, [markersOnMissClickHighlight]);

    useEffect(() => {
        ctxModal.setVisibleStoryIds(visibleStoryIds);
        // It's important to check the exactly content to avoid infinite loop
    }, [visibleStoryIds.join(';')]);

    const markers = (ctxView.allMarkers ?? []).map<WorldMapMarkerStory_worldMapView_markers>(
        (marker) => ({
            ...marker,
            selectable:
                markerVisibility.isMarkerSelectableInView(marker) ||
                props.customClickableMarkerIds?.has(marker.worldMapMarkerId),
            visible: markerVisibility.isMarkerVisibleInViewMap(marker)
        })
    );

    let showMarkers: IWorldMarker[] = [];
    if (props.overrideMapMarkers) {
        showMarkers = props.overrideMapMarkers;
    } else if (markers) {
        showMarkers = mapGraphQLMarkers(markers);
    }

    showMarkers = showMarkers.map((m) => {
        if (m.elementType === 'indoor_stand') {
            return mapIndoorStandForViewAndEdit(m);
        }
        if (m.elementType === 'v2/variant_icon') {
            return mapVariantIconForViewAndEdit(m, ctxView.allDetails);
        }
        return m;
    });

    const initialViewState = getInitialViewState(ctxView.map, isWide);

    return (
        <S_CustomMap>
            {props.topOverflow && (
                <S_TopOverflow $position={props.topOverflowPosition ?? 'left'}>
                    {props.topOverflow}
                </S_TopOverflow>
            )}
            <LazyImageFallback mapRef={ctxView.mapRef} markers={showMarkers} />
            <GiveMeMap
                key={ctxView.map.worldMapId}
                mapRef={ctxView.mapRef}
                isWide={isWide}
                markerGlobalSettings={GLOBAL_MARKER_CONFIG[ctxView.map.worldMapId]}
                language={props.language}
                map={{
                    // Those are only clickable in view, not in editor
                    // v1 => 'clusters', v2 => 'icons|cluster'
                    interactiveLayerIds: extraInteractiveLayerIds.concat('icons|cluster'),
                    initialViewState,
                    light: {
                        anchor: 'viewport',
                        color: 'white',
                        intensity: 0.2
                    },
                    antialias: false,
                    minZoom: isWide
                        ? ctxView.map.initial.minZoom
                        : ctxView.map.initialMobile.minZoom,
                    maxZoom: isWide
                        ? ctxView.map.initial.maxZoom
                        : ctxView.map.initialMobile.maxZoom,
                    bearing: ctxView.map.initial.rotation ?? 0,
                    pitch: ctxView.map.initial.pitch ?? 0,
                    maxBounds:
                        props.forceInitialPositionBounds && initialViewState.bounds
                            ? initialViewState.bounds
                            : null,
                    mapboxAccessToken: process.env.REACT_APP_MAPBOX_TOKEN,
                    onLoad: () => {
                        props.onMapLoaded?.(ctxView.mapRef.current);
                    },
                    cooperativeGestures: props.disableSimpleGestures,
                    ...(props.mapProps ?? {}),
                    locale: {
                        'ScrollZoomBlocker.CtrlMessage':
                            Localization.MAPBOX_SCROLL_ZOOM_BLOCKER_CTRL,
                        'ScrollZoomBlocker.CmdMessage':
                            Localization.MAPBOX_SCROLL_ZOOM_BLOCKER_COMMAND
                    }
                }}
                callbacks={{
                    onMarkersSelected
                }}
                selectableMarkersStyle={
                    markersOnMissClickHighlight || ctxView.forceMarkersHighlight
                        ? {
                              shadowColor: 'rgb(255 238 85)',
                              pixelSize: 35
                          }
                        : undefined
                }
                highlightedMarkersStyle={{
                    shadowColor: 'rgb(255 238 85)',
                    pixelSize: 35
                }}
                forceHighlightSelectableMarkers={
                    markersOnMissClickHighlight || ctxView.forceMarkersHighlight
                }
                highlightedMarkers={(ctxModal.detailModal?.detail?.connectedMarkers ?? [])
                    .map((e) => e.worldMapMarkerId)
                    .concat(props.highlightedMarkerIds ?? [])}
                orderedMarkerIds={props.orderedMarkerIds ?? []}
                markers={showMarkers
                    .concat(
                        backgroundImageMarker.getBackgroundMarker(
                            ctxView.map.defaultStyles,
                            ctxView.map.imageBackgroundUrl,
                            ctxView.map.imageBackgroundCoordinates
                        )
                    )
                    .filter(Boolean)}
                customBuilders={{
                    reactMarkers: CUSTOM_MARKER_BUILDERS,
                    pinIcon: pinIconBuilder
                }}
                fonts={{
                    regular: EFonts.Regular,
                    semiBold: EFonts.InterSemiBold,
                    bold: EFonts.Bold
                }}
                config={{
                    availableStyles: ctxView.map?.defaultStyles?.map(
                        (e) => GraphQLStylesMapToEnum[e] ?? e
                    ),
                    markersCustomConfig: props.markersCustomConfig,
                    geolocate: ctxView.map?.allowGeolocation,
                    groupMarkerProps: {
                        textColor: ctxView.map.groupMarkerColor,
                        backgroundColor: ctxView.map.groupMarkerBackground,
                        clusterMaxZoom: ctxView.map.groupMarkerClusterMaxZoom,
                        clusterRadius: ctxView.map.groupMarkerClusterRadius,
                        autoHideIcons: ctxView.map.autoHideIcons
                    },
                    countriesFill:
                        ctxView.map.fillCountryCodes?.length &&
                        ctxView.map.fillCountryColor &&
                        ctxView.map.fillCountryOpacity
                            ? {
                                  countryCodes: ctxView.map.fillCountryCodes,
                                  opacity: ctxView.map.fillCountryOpacity,
                                  color: ctxView.map.fillCountryColor
                              }
                            : null
                }}
            >
                {props.mapChildren}
                {(ctxView.customPageConfig.markerDetailStyle
                    ? ctxView.customPageConfig?.markerDetailStyle === 'Bubble'
                    : ctxView.map?.markerDetailStyle === WorldMapMarkerDetailStyle.BUBBLE) &&
                    ctxView.allMarkers && (
                        <DetailModalBubble
                            mapRef={ctxView.mapRef}
                            allMarkers={ctxView.allMarkers.map(mapGraphQLMarker)}
                        />
                    )}
            </GiveMeMap>
        </S_CustomMap>
    );
};

const S_CustomMap = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;

const S_TopOverflow = styled.div<{ $position: 'left' | 'right' }>`
    position: absolute;
    top: 0;
    max-width: 100%;
    z-index: 2;
    pointer-events: none;
    padding: 10px;

    ${(props) => {
        if (props.$position === 'right') {
            return css`
                right: 0;
            `;
        }
        return css`
            left: 0;
        `;
    }}
`;
